import { NavLink, useLocation } from 'react-router';
import { m } from 'motion/react';
import { useAuth } from '@/auth/AuthProvider';
import { LogoDisc, Wordmark } from '@/design/components/Logo';
import { Tooltip } from '@/design/components/Controls';
import { cx } from '@/lib/cx';
import { NAV, QUICK_ACTIONS, type NavItem } from './nav';

export function useVisibleNav() {
  const { can } = useAuth();
  const visible = (i: NavItem) => !i.anyOf || i.anyOf.some((c) => (i.unrestricted ? can(c, null) : can(c)));
  return {
    sections: NAV.map((s) => ({ ...s, items: s.items.filter(visible) })).filter((s) => s.items.length > 0),
    actions: QUICK_ACTIONS.filter(visible),
  };
}

export function isActive(pathname: string, item: NavItem): boolean {
  if (pathname === item.to) return true;
  return !!item.prefix && pathname.startsWith(item.to + '/') && !pathname.startsWith('/presupuestos/nuevo');
}

export function Sidebar() {
  const { pathname } = useLocation();
  const { sections, actions } = useVisibleNav();

  return (
    <aside className="sticky top-0 hidden h-dvh w-[260px] shrink-0 flex-col border-r border-line bg-surface-0/80 backdrop-blur lg:flex">
      <div className="marble relative flex items-center gap-3 border-b border-line px-5 py-5">
        <LogoDisc size={44} layoutId="brand-disc" />
        <Wordmark />
        <div className="gold-hairline absolute inset-x-5 bottom-0" />
      </div>

      {actions.map((a) => (
        <div key={a.to} className="px-4 pt-4" data-tour="new-quote">
          <NavLink
            to={a.to}
            className="gold-gradient sheen-host flex h-10 items-center justify-center gap-2 rounded-(--radius-button) border border-gold-600/40 text-sm font-semibold text-ink-900 shadow-gold transition active:scale-[0.98]"
          >
            <a.icon className="relative z-[2] size-4" />
            <span className="relative z-[2]">{a.label}</span>
          </NavLink>
        </div>
      ))}

      <nav data-tour="nav" className="scrollbar-thin flex-1 overflow-y-auto px-3 py-4" aria-label="Principal">
        {sections.map((section, si) => (
          <div key={si} className="mb-5">
            {section.title && <div className="overline mb-1.5 px-3 !text-[10.5px] !text-ink-400">{section.title}</div>}
            <ul className="space-y-0.5">
              {section.items.map((item) => {
                const active = isActive(pathname, item);
                const link = (
                    <NavLink
                      to={item.to}
                      className={cx(
                        'group relative flex h-9 items-center gap-3 rounded-[9px] px-3 text-[13.5px] transition-colors',
                        active ? 'font-semibold text-ink-900' : 'text-ink-500 hover:bg-surface-2 hover:text-ink-900',
                      )}
                    >
                      {active && (
                        <m.span
                          layoutId="nav-active"
                          className="absolute inset-0 rounded-[9px] border border-gold-200 bg-gold-50/70 shadow-e1"
                          transition={{ type: 'spring', stiffness: 500, damping: 40 }}
                        />
                      )}
                      {active && (
                        <m.span
                          layoutId="nav-bar"
                          className="absolute top-2 bottom-2 -left-3 w-[3px] rounded-r-full bg-gold-500"
                          transition={{ type: 'spring', stiffness: 500, damping: 40 }}
                        />
                      )}
                      <item.icon
                        className={cx(
                          'relative size-[18px] transition-transform duration-200 group-hover:scale-110',
                          active ? 'text-gold-600' : 'text-ink-400',
                        )}
                        strokeWidth={1.75}
                      />
                      <span className="relative">{item.label}</span>
                    </NavLink>
                );
                return <li key={item.to}>{item.hint ? <Tooltip content={item.hint} side="right">{link}</Tooltip> : link}</li>;
              })}
            </ul>
          </div>
        ))}
      </nav>
      <UserCard />
    </aside>
  );
}

function UserCard() {
  const { me } = useAuth();
  if (!me) return null;
  const initials = me.user.name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
  return (
    <div className="flex items-center gap-3 border-t border-line px-4 py-3">
      <div className="flex size-8 items-center justify-center rounded-full border border-gold-200 bg-gold-50 text-[12px] font-semibold text-gold-700">
        {initials}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-medium">{me.user.name}</div>
        <div className="truncate text-[11.5px] text-ink-400">{me.user.email}</div>
      </div>
    </div>
  );
}
